import React, { Component, Fragment } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Voiceover from '../Voiceover';

export default function(WrappedComponent) {
  class Slide extends Component {
    constructor(props) {
      super(props);

      this.slideTimerInterval = null;
      this.timeElapsedBase = 0;
      this.timePlayed = 0;
      this.greensockObject = null;

      this.state = {
        voiceoverCanPlayThrough: !this.props.vo
      };
    }

    componentDidMount() {
      this.props.updateCanPlayThrough(
        this.props.index,
        this.state.voiceoverCanPlayThrough
      );
    }

    componentDidUpdate(prevProps) {
      if (!prevProps.paused && this.props.paused) {
        this.advanceSlideTimer();
        clearInterval(this.slideTimerInterval);

        if (this.greensockObject) {
          this.greensockObject.pause();
        }
      } else if (prevProps.paused && !this.props.paused) {
        this.timeElapsedBase = performance.now();
        this.slideTimerInterval = setInterval(this.advanceSlideTimer, 500);

        if (this.greensockObject) {
          if (this.props.hasPlayedThrough) {
            this.greensockObject.restart();
          } else {
            this.greensockObject.resume();
          }
        }
      }

      this.props.updateCanPlayThrough(
        this.props.index,
        this.state.voiceoverCanPlayThrough
      );
    }

    componentWillUnmount() {
      // Make sure interval and GSAP is cleared if user navigates away in the middle of a slide playing
      clearInterval(this.slideTimerInterval);
      if (this.greensockObject) {
        this.greensockObject.kill();
      }
    }

    advanceSlideTimer = () => {
      if (!this.timeElapsedBase) return;

      const slideDuration = this.getSlideDurationInMs();
      const newTimestamp = performance.now();
      this.timePlayed += newTimestamp - this.timeElapsedBase;
      this.timeElapsedBase = newTimestamp;

      if (this.timePlayed > slideDuration) {
        clearInterval(this.slideTimerInterval);
        this.timeElapsedBase = 0;
        this.timePlayed = 0;
        this.props.onComplete(this.props.index);
      }
    };

    getSlideDurationInMs = () => {
      return this.props.duration * 1000;
    };

    getSlideAnimationClassNames = slideTypeName => {
      return classNames(
        'slide__content__animation',
        'slide__content__animation-type--' + slideTypeName
      );
    };

    getSlideTextClassNames = textTypeName => {
      return classNames(
        'slide__content__text',
        'slide__content__text-type--' + textTypeName
      );
    };

    getSlideTextMarkup = (componentName, text, setRefFunc) => {
      const textBoxes =
        typeof text === 'string' ? (
          <div className="slide__content__text__box">{text}</div>
        ) : (
          text
        );

      return (
        <div className={this.getSlideTextClassNames(componentName)}>
          <div className="slide__content__text__boxes-min-height-clamp" />
          <div className="slide__content__text__boxes-wrapper" ref={setRefFunc}>
            {textBoxes}
          </div>
        </div>
      );
    };

    setGreensockObject = element => {
      this.greensockObject = element;
    };

    voiceoverCanPlayThrough = canPlayThrough => {
      if (canPlayThrough !== this.state.voiceoverCanPlayThrough) {
        this.setState({
          voiceoverCanPlayThrough: canPlayThrough
        });
      }
      if (this.props.hasPlayedThrough) {
        this.setState({
          voiceoverCanPlayThrough: true
        });
      }
    };

    render() {
      const {
        // Don't pass through
        bg,
        hidden,
        index,
        duration,
        paused,
        text,
        vo,
        ...passThroughProps
      } = this.props;

      const childrenPaused = paused || hidden;

      const bgMarkup = bg ? (
        <div className="slide__bg">
          <img alt="" src={bg} />
        </div>
      ) : null;

      const voiceoverMarkup = vo ? (
        <Voiceover
          {...vo}
          hidden={hidden}
          autoPlay={false}
          paused={childrenPaused}
          onCanPlayThrough={this.voiceoverCanPlayThrough}
          hasPlayedThrough={this.props.hasPlayedThrough}
        />
      ) : null;

      const textMarkup = text ? (
        <text.type
          {...text}
          markupFunc={this.getSlideTextMarkup}
          paused={childrenPaused}
        />
      ) : null;

      return (
        <Fragment>
          {voiceoverMarkup}
          <div className="slide" hidden={hidden}>
            {bgMarkup}

            <div className="slide__content">
              <WrappedComponent
                {...passThroughProps}
                classNameFunc={this.getSlideAnimationClassNames}
                duration={duration}
                setGreensockObjectFunc={this.setGreensockObject}
                paused={childrenPaused}
              />
              {textMarkup}
            </div>
          </div>
        </Fragment>
      );
    }
  }

  Slide.propTypes = {
    bg: PropTypes.string,
    duration: PropTypes.number.isRequired,
    hidden: PropTypes.bool.isRequired,
    index: PropTypes.number.isRequired,
    paused: PropTypes.bool.isRequired,
    text: PropTypes.object,
    vo: PropTypes.object
  };

  return Slide;
}



// WEBPACK FOOTER //
// ../core/src/components/Slideshow/Slide.js